// src/pages/retail-shops/ManualDebits.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Typography, Grid, Button, Chip, IconButton, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  FormControl, InputLabel, Select, LinearProgress, Alert, Snackbar,
  TablePagination, InputAdornment, CircularProgress, Fade, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Avatar, alpha, Divider, Stack, Card, CardContent, Skeleton,
  Collapse, Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  FilterAlt as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  RemoveCircle as RemoveCircleIcon,
  AccountBalanceWallet as WalletIcon,
  Receipt as ReceiptIcon,
  TrendingDown as TrendingDownIcon,
  CalendarMonth as CalendarIcon,
  AdminPanelSettings as AdminIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

import '../../styles/Waiters.css';
import Sidebar from '../../components/finance/Sidebar';
import Header from '../../components/finance/Header';
import Footer from '../../components/finance/Footer';
import { ACCE_BRAND, glossyButton } from '../../theme/colors';

// ════════════════════════════════════════════
// API
// ════════════════════════════════════════════
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';

const api = axios.create({
  baseURL: `${API_BASE_URL}${API_PREFIX}`,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const extractError = (err) => {
  if (!err) return 'Request failed';
  if (err.code === 'ECONNABORTED') return 'Request timed out. Please try again.';
  if (err.message === 'Network Error') return 'Network error — is the server running?';
  const res = err.response;
  if (!res) return err.message || 'Request failed';
  const d = res.data;
  if (typeof d === 'string') {
    const t = d.match(/<title>([^<]+)<\/title>/i);
    if (t) return t[1].trim();
    return d.slice(0, 200) || `Request failed (${res.status})`;
  }
  if (d && typeof d === 'object') {
    if (d.errors) {
      const first = Object.values(d.errors)[0];
      return Array.isArray(first) ? first[0] : String(first);
    }
    if (d.message) return d.message;
    if (d.error) return String(d.error);
  }
  return `Request failed (HTTP ${res.status})`;
};

const apiManualDebits = async (params = {}) => {
  try {
    const { data } = await api.get('/tuckshop/wallets/manual-debits', { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiManualDebitsStats = async (params = {}) => {
  try {
    const { data } = await api.get('/tuckshop/wallets/manual-debits/stats', { params });
    return { success: true, stats: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

// ════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════
const REASON_OPTIONS = [
  { value: 'charge',     label: 'Charge',     color: '#f57c00' },
  { value: 'adjustment', label: 'Adjustment', color: '#1976d2' },
  { value: 'penalty',    label: 'Penalty',    color: '#c62828' },
  { value: 'correction', label: 'Correction', color: '#7b1fa2' },
  { value: 'other',      label: 'Other',      color: '#546e7a' },
];

const reasonColor = (code) => {
  const r = REASON_OPTIONS.find((x) => x.value === code);
  return r?.color || '#546e7a';
};

const reasonLabel = (code) => {
  const r = REASON_OPTIONS.find((x) => x.value === code);
  return r?.label || code || '—';
};

// ════════════════════════════════════════════
// UTILITIES
// ════════════════════════════════════════════
const formatCurrency = (v) => {
  if (v === null || v === undefined || v === '') return '₦0.00';
  return `₦${Number(v).toLocaleString('en-NG', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

const formatCompactCurrency = (v) => {
  const n = Number(v) || 0;
  if (Math.abs(n) >= 1_000_000) return `₦${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000)     return `₦${(n / 1_000).toFixed(1)}K`;
  return `₦${n.toFixed(0)}`;
};

const formatDateTime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const initials = (name) => {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2)
    .map((s) => s[0]).join('').toUpperCase();
};

/**
 * Convert a JS Date to a yyyy-MM-dd string suitable for <input type="date">.
 */
const dateToInputValue = (d) => {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(dt.getTime())) return '';
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const day = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/**
 * Parse a yyyy-MM-dd string from <input type="date"> into a local Date (no TZ surprises).
 */
const inputValueToDate = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d, 0, 0, 0, 0);
};

/** Escape a value for CSV output */
const csvEscape = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/** Format a Date to yyyyMMdd-HHmm for filenames */
const fileStamp = (d = new Date()) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${y}${m}${day}-${hh}${mm}`;
};

// ════════════════════════════════════════════
// SUB-COMPONENTS
// ════════════════════════════════════════════
const StatCard = React.memo(({ label, value, sub, icon, color, active }) => (
  <Card
    elevation={0}
    sx={{
      borderRadius: '14px',
      border: `1px solid ${alpha(color, active ? 0.5 : 0.15)}`,
      bgcolor: active ? alpha(color, 0.04) : '#fff',
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 0.25s ease',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: `0 12px 30px ${alpha(color, 0.12)}`,
        borderColor: alpha(color, 0.4),
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0, top: 0, bottom: 0, width: 4,
        bgcolor: color,
      },
    }}
  >
    <CardContent sx={{ p: 2, pl: 2.4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="caption" sx={{
            color: '#64748B', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '0.6px', fontSize: '0.65rem',
            fontFamily: '"Inter", sans-serif', display: 'block', mb: 0.5,
          }}>
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={800} sx={{
            fontSize: { xs: '1.05rem', sm: '1.3rem' },
            color: '#0F172A', lineHeight: 1.15,
            fontFamily: '"Inter", sans-serif', letterSpacing: '-0.5px',
          }}>
            {value}
          </Typography>
          {sub && (
            <Typography variant="caption" sx={{
              color: '#64748B', fontWeight: 600, fontSize: '0.68rem',
              fontFamily: '"Inter", sans-serif', display: 'block', mt: 0.4,
            }}>
              {sub}
            </Typography>
          )}
        </Box>
        <Avatar sx={{
          bgcolor: alpha(color, 0.1), color,
          width: 40, height: 40, borderRadius: '10px',
        }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
));
StatCard.displayName = 'StatCard';

// ════════════════════════════════════════════
// DETAIL DIALOG
// ════════════════════════════════════════════
const DebitDetailDialog = ({ open, debit, onClose }) => {
  if (!debit) return null;
  const reason = reasonLabel(debit.reason_code);
  const rColor = reasonColor(debit.reason_code);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ bgcolor: alpha('#c62828', 0.12), color: '#c62828' }}>
            <ReceiptIcon />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={800} fontFamily='"Inter", sans-serif'>
              Debit Details
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {debit.reference}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        <Paper variant="outlined" sx={{
          p: 2, borderRadius: 2, mb: 3,
          borderColor: alpha('#c62828', 0.2),
          bgcolor: alpha('#c62828', 0.02),
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Amount Debited</Typography>
            <Typography variant="h5" fontWeight={800} sx={{ color: '#c62828', fontFamily: '"Inter", sans-serif' }}>
              −{formatCurrency(debit.amount)}
            </Typography>
          </Stack>
        </Paper>

        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, fontFamily: '"Inter", sans-serif' }}>
          Student
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Avatar src={debit.user_avatar || undefined}
            sx={{ width: 48, height: 48, bgcolor: ACCE_BRAND.orange, fontWeight: 800 }}>
            {initials(debit.holder_name)}
          </Avatar>
          <Box>
            <Typography fontWeight={800} fontFamily='"Inter", sans-serif'>
              {debit.holder_name}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {debit.staff_id}
            </Typography>
          </Box>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Reason Category</Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip
                size="small"
                label={reason}
                sx={{
                  fontWeight: 700, fontSize: '0.7rem',
                  bgcolor: alpha(rColor, 0.1), color: rColor,
                }}
              />
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Date</Typography>
            <Typography variant="body2" sx={{ fontFamily: '"Inter", sans-serif', mt: 0.5 }}>
              {formatDateTime(debit.occurred_at)}
            </Typography>
          </Grid>
        </Grid>

        <Typography variant="caption" color="text.secondary" fontWeight={700}>Reason Note</Typography>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mt: 0.5, mb: 2 }}>
          <Typography variant="body2" sx={{ fontFamily: '"Inter", sans-serif' }}>
            {debit.reason_note || debit.description || '—'}
          </Typography>
        </Paper>

        <Typography variant="caption" color="text.secondary" fontWeight={700}>Performed By</Typography>
        <Typography variant="body2" sx={{ fontFamily: '"Inter", sans-serif', mb: 2 }}>
          {debit.performed_by_name || '—'}
          {debit.performed_by_email && (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              ({debit.performed_by_email})
            </Typography>
          )}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Balance Before</Typography>
            <Typography fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.85rem' }}>
              {formatCurrency(debit.balance_before)}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Balance After</Typography>
            <Typography fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif', fontSize: '0.85rem', color: '#c62828' }}>
              {formatCurrency(debit.balance_after)}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Reference</Typography>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75rem' }}>
              {debit.reference}
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', fontWeight: 700 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════
const ManualDebits = () => {
  const [activeMenu, setActiveMenu] = useState('Wallet');

  // Data
  const [debits, setDebits] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [searchValue, setSearchValue] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [fromDate, setFromDate] = useState(null);   // Date | null
  const [toDate, setToDate] = useState(null);       // Date | null
  const [minAmount, setMinAmount] = useState('');
  const [maxAmount, setMaxAmount] = useState('');
  const [sort, setSort] = useState('occurred_at');
  const [order, setOrder] = useState('desc');
  const [showMoreFilters, setShowMoreFilters] = useState(false);

  // Detail dialog
  const [detailDebit, setDetailDebit] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const debounceRef = useRef(null);

  const pushSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = useCallback(() => setSnackbar((s) => ({ ...s, open: false })), []);

  // Debounce search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearchDebounced(searchValue.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchValue]);

  // Build query params from filters
  const buildParams = useCallback(({ includePaging = true } = {}) => {
    const params = {};
    if (includePaging) {
      params.page = page + 1;
      params.page_size = rowsPerPage;
    }
    params.sort = sort;
    params.order = order;
    if (searchDebounced) params.search = searchDebounced;
    if (reasonCode) params.reason_code = reasonCode;
    if (fromDate) params.from = dateToInputValue(fromDate);
    if (toDate) params.to = dateToInputValue(toDate);
    if (minAmount && /^\d+(\.\d{1,2})?$/.test(minAmount)) params.min_amount = minAmount;
    if (maxAmount && /^\d+(\.\d{1,2})?$/.test(maxAmount)) params.max_amount = maxAmount;
    return params;
  }, [page, rowsPerPage, searchDebounced, reasonCode, fromDate, toDate, minAmount, maxAmount, sort, order]);

  // Fetch list
  const fetchDebits = useCallback(async () => {
    setLoading(true);
    const res = await apiManualDebits(buildParams({ includePaging: true }));
    setLoading(false);
    if (res.success) {
      setDebits(res.data || []);
      setTotal(res.total || 0);
    } else {
      pushSnackbar(res.error || 'Failed to load manual debits', 'error');
      setDebits([]);
      setTotal(0);
    }
  }, [buildParams, pushSnackbar]);

  // Fetch stats (same filters, no pagination)
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    const res = await apiManualDebitsStats(buildParams({ includePaging: false }));
    setStatsLoading(false);
    if (res.success) setStats(res.stats);
  }, [buildParams]);

  useEffect(() => { fetchDebits(); }, [fetchDebits]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Reset filters
  const resetFilters = useCallback(() => {
    setSearchValue('');
    setSearchDebounced('');
    setReasonCode('');
    setFromDate(null);
    setToDate(null);
    setMinAmount('');
    setMaxAmount('');
    setSort('occurred_at');
    setOrder('desc');
    setPage(0);
  }, []);

  // Quick presets
  const applyPreset = useCallback((preset) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    switch (preset) {
      case 'today':
        setFromDate(today);
        setToDate(today);
        break;
      case 'last7': {
        const start = new Date(today);
        start.setDate(start.getDate() - 7);
        setFromDate(start);
        setToDate(today);
        break;
      }
      case 'thisMonth': {
        const start = new Date(today.getFullYear(), today.getMonth(), 1);
        setFromDate(start);
        setToDate(today);
        break;
      }
      case 'all':
        setFromDate(null);
        setToDate(null);
        break;
      default:
        break;
    }
    setPage(0);
  }, []);

  const handleRefresh = useCallback(() => {
    fetchDebits();
    fetchStats();
    pushSnackbar('Refreshed', 'info');
  }, [fetchDebits, fetchStats, pushSnackbar]);

  const activeFiltersCount = useMemo(() => {
    let n = 0;
    if (reasonCode) n++;
    if (fromDate) n++;
    if (toDate) n++;
    if (minAmount) n++;
    if (maxAmount) n++;
    if (sort !== 'occurred_at' || order !== 'desc') n++;
    return n;
  }, [reasonCode, fromDate, toDate, minAmount, maxAmount, sort, order]);

  // ── CSV export of current page ──
  const handleExportCsv = useCallback(() => {
    if (!debits.length) {
      pushSnackbar('Nothing to export.', 'warning');
      return;
    }
    const headers = [
      'Date', 'Reference', 'Student ID', 'Holder Name',
      'Amount', 'Balance Before', 'Balance After',
      'Reason Code', 'Reason Note', 'Performed By', 'Performed By Email',
    ];
    const rows = debits.map((d) => [
      formatDateTime(d.occurred_at),
      d.reference,
      d.staff_id,
      d.holder_name,
      d.amount,
      d.balance_before,
      d.balance_after,
      d.reason_code || '',
      d.reason_note || d.description || '',
      d.performed_by_name || '',
      d.performed_by_email || '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `manual-debits-${fileStamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    pushSnackbar(`Exported ${debits.length} row${debits.length === 1 ? '' : 's'}.`, 'success');
  }, [debits, pushSnackbar]);

  return (
    <div className="epos-wrapper">
      <div className="epos-main-layout">
        <Sidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

        <div className="epos-main">
          <Header />

          <div className="epos-content" style={{ background: '#f5f7fa' }}>
            {/* HERO */}
            <div className="hero-card">
              <div>
                <div className="hero-title" style={{ color: '#c62828', fontWeight: 800 }}>
                  🔻 Manual Wallet Debits
                </div>
                <div className="hero-text-wrapper">
                  <div className="hero-tagline">
                    <span className="hero-tagline-icon">✦</span>
                    Every admin deduction · traceable, filterable, auditable
                  </div>
                </div>
              </div>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip title="Export CSV">
                  <IconButton
                    onClick={handleExportCsv}
                    sx={{
                      bgcolor: alpha('#1976d2', 0.1),
                      '&:hover': { bgcolor: alpha('#1976d2', 0.2) },
                      borderRadius: 2,
                    }}
                  >
                    <DownloadIcon sx={{ color: '#1976d2' }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      bgcolor: alpha('#c62828', 0.1),
                      '&:hover': { bgcolor: alpha('#c62828', 0.2) },
                      borderRadius: 2,
                    }}
                  >
                    <RefreshIcon sx={{ color: '#c62828' }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </div>

            {/* STATS CARDS */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  label="Today"
                  value={formatCompactCurrency(stats?.today?.total_amount || 0)}
                  sub={`${stats?.today?.count ?? 0} debit${stats?.today?.count === 1 ? '' : 's'}`}
                  icon={<CalendarIcon />}
                  color="#c62828"
                  active
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  label="Last 7 Days"
                  value={formatCompactCurrency(stats?.week?.total_amount || 0)}
                  sub={`${stats?.week?.count ?? 0} debits`}
                  icon={<TrendingDownIcon />}
                  color="#f57c00"
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  label="This Month"
                  value={formatCompactCurrency(stats?.month?.total_amount || 0)}
                  sub={`${stats?.month?.count ?? 0} debits`}
                  icon={<CalendarIcon />}
                  color="#1976d2"
                />
              </Grid>
              <Grid item xs={6} sm={6} md={3}>
                <StatCard
                  label="All Time"
                  value={formatCompactCurrency(stats?.all_time?.total_amount || 0)}
                  sub={`${stats?.all_time?.count ?? 0} debits total`}
                  icon={<WalletIcon />}
                  color="#37474f"
                />
              </Grid>
            </Grid>

            {/* REASON BREAKDOWN */}
            {stats?.by_reason?.length > 0 && (
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5, fontFamily: '"Inter", sans-serif' }}>
                  Breakdown by reason
                </Typography>
                <Grid container spacing={2}>
                  {stats.by_reason.map((r) => (
                    <Grid item xs={6} sm={4} md={3} lg={2.4} key={r.reason_code}>
                      <Box
                        onClick={() => {
                          setReasonCode(reasonCode === r.reason_code ? '' : r.reason_code);
                          setPage(0);
                        }}
                        sx={{
                          p: 1.5, borderRadius: 2, cursor: 'pointer',
                          border: '1px solid',
                          borderColor: reasonCode === r.reason_code
                            ? alpha(reasonColor(r.reason_code), 0.5)
                            : 'divider',
                          bgcolor: reasonCode === r.reason_code
                            ? alpha(reasonColor(r.reason_code), 0.06)
                            : '#fff',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: alpha(reasonColor(r.reason_code), 0.4),
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                          <Box sx={{
                            width: 10, height: 10, borderRadius: '50%',
                            bgcolor: reasonColor(r.reason_code),
                          }} />
                          <Typography variant="caption" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                            {reasonLabel(r.reason_code)}
                          </Typography>
                        </Stack>
                        <Typography fontWeight={800} sx={{
                          color: reasonColor(r.reason_code),
                          fontFamily: '"Inter", sans-serif', fontSize: '0.95rem',
                        }}>
                          {formatCurrency(r.total_amount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {r.count} debit{r.count === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}

            {/* TOP ADMINS */}
            {stats?.top_admins?.length > 0 && (
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                  <AdminIcon sx={{ color: '#1976d2', fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                    Top admins by amount debited
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
                  {stats.top_admins.map((a, i) => (
                    <Paper
                      key={a.email || i}
                      variant="outlined"
                      sx={{
                        p: 1.5, borderRadius: 2, minWidth: 200,
                        borderColor: 'divider',
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.2}>
                        <Avatar sx={{
                          width: 32, height: 32, bgcolor: alpha('#1976d2', 0.1),
                          color: '#1976d2', fontWeight: 800, fontSize: '0.75rem',
                        }}>
                          {initials(a.name || a.email)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" fontWeight={800} noWrap
                            sx={{ fontFamily: '"Inter", sans-serif', display: 'block' }}>
                            {a.name || a.email}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {a.count} debits · {formatCompactCurrency(a.total_amount)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </Paper>
            )}

            {/* FILTERS */}
            <Paper sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by name, student ID, or email…"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  sx={{
                    flex: 1, minWidth: 240,
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: searchValue && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchValue('')}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel sx={{ fontWeight: 700 }}>Reason</InputLabel>
                  <Select
                    value={reasonCode}
                    label="Reason"
                    onChange={(e) => { setReasonCode(e.target.value); setPage(0); }}
                    sx={{ borderRadius: 2, fontWeight: 600 }}
                  >
                    <MenuItem value="" sx={{ fontWeight: 600 }}>All reasons</MenuItem>
                    {REASON_OPTIONS.map((r) => (
                      <MenuItem key={r.value} value={r.value} sx={{ fontWeight: 600 }}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: r.color }} />
                          <span>{r.label}</span>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant={showMoreFilters ? 'contained' : 'outlined'}
                  onClick={() => setShowMoreFilters((v) => !v)}
                  startIcon={<FilterIcon />}
                  endIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    textTransform: 'none', fontWeight: 700, borderRadius: 2,
                    minWidth: 140,
                    ...(showMoreFilters ? {
                      bgcolor: '#c62828', color: '#fff',
                      '&:hover': { bgcolor: '#8e0000' },
                    } : {}),
                  }}
                >
                  <Badge badgeContent={activeFiltersCount} color="warning"
                    invisible={activeFiltersCount === 0} sx={{ '& .MuiBadge-badge': { right: -8, top: 4 } }}>
                    Filters
                  </Badge>
                </Button>
                {activeFiltersCount > 0 && (
                  <Button
                    variant="text"
                    onClick={resetFilters}
                    startIcon={<ClearIcon />}
                    sx={{ textTransform: 'none', fontWeight: 700, color: '#c62828' }}
                  >
                    Clear all
                  </Button>
                )}
              </Stack>

              <Collapse in={showMoreFilters} unmountOnExit>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth size="small" type="date"
                      label="From date"
                      value={dateToInputValue(fromDate)}
                      onChange={(e) => {
                        setFromDate(inputValueToDate(e.target.value));
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth size="small" type="date"
                      label="To date"
                      value={dateToInputValue(toDate)}
                      onChange={(e) => {
                        setToDate(inputValueToDate(e.target.value));
                        setPage(0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      fullWidth size="small" type="number"
                      label="Min amount"
                      value={minAmount}
                      onChange={(e) => { setMinAmount(e.target.value); setPage(0); }}
                      placeholder="0.00"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={6} sm={3} md={2}>
                    <TextField
                      fullWidth size="small" type="number"
                      label="Max amount"
                      value={maxAmount}
                      onChange={(e) => { setMaxAmount(e.target.value); setPage(0); }}
                      placeholder="0.00"
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontWeight: 700 }}>Sort by</InputLabel>
                      <Select
                        value={sort}
                        label="Sort by"
                        onChange={(e) => { setSort(e.target.value); setPage(0); }}
                        sx={{ borderRadius: 2, fontWeight: 600 }}
                      >
                        <MenuItem value="occurred_at" sx={{ fontWeight: 600 }}>Date</MenuItem>
                        <MenuItem value="amount" sx={{ fontWeight: 600 }}>Amount</MenuItem>
                        <MenuItem value="created_at" sx={{ fontWeight: 600 }}>Created</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel sx={{ fontWeight: 700 }}>Order</InputLabel>
                      <Select
                        value={order}
                        label="Order"
                        onChange={(e) => { setOrder(e.target.value); setPage(0); }}
                        sx={{ borderRadius: 2, fontWeight: 600 }}
                      >
                        <MenuItem value="desc" sx={{ fontWeight: 600 }}>Descending</MenuItem>
                        <MenuItem value="asc"  sx={{ fontWeight: 600 }}>Ascending</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ mr: 1, alignSelf: 'center' }}>
                        Quick range:
                      </Typography>
                      <Button size="small" onClick={() => applyPreset('today')} sx={{ textTransform: 'none', fontWeight: 700 }}>Today</Button>
                      <Button size="small" onClick={() => applyPreset('last7')} sx={{ textTransform: 'none', fontWeight: 700 }}>Last 7 days</Button>
                      <Button size="small" onClick={() => applyPreset('thisMonth')} sx={{ textTransform: 'none', fontWeight: 700 }}>This month</Button>
                      <Button size="small" onClick={() => applyPreset('all')} sx={{ textTransform: 'none', fontWeight: 700 }}>All time</Button>
                    </Stack>
                  </Grid>
                </Grid>
              </Collapse>
            </Paper>

            {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

            {/* TABLE */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{
                p: 2, display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', flexWrap: 'wrap', gap: 1,
              }}>
                <Typography variant="subtitle2" fontWeight={800} sx={{ fontFamily: '"Inter", sans-serif' }}>
                  {total} manual debit{total === 1 ? '' : 's'}
                </Typography>
                {stats && (
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    Total: {formatCurrency(stats.all_time?.total_amount || 0)}
                  </Typography>
                )}
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha('#c62828', 0.04) }}>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Student</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Student ID</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Reason</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Note</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Amount</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Balance After</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}>By</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 800, fontFamily: '"Inter", sans-serif' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading && debits.length === 0 ? (
                      [...Array(6)].map((_, i) => (
                        <TableRow key={i}>
                          {[...Array(9)].map((__, j) => (
                            <TableCell key={j}><Skeleton variant="text" /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : debits.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} align="center" sx={{ py: 8 }}>
                          <RemoveCircleIcon sx={{ fontSize: 60, color: 'text.disabled', opacity: 0.4, mb: 1 }} />
                          <Typography variant="h6" color="text.secondary" fontWeight={700} fontFamily='"Inter", sans-serif'>
                            No manual debits found
                          </Typography>
                          <Typography variant="body2" color="text.secondary" fontFamily='"Inter", sans-serif'>
                            {activeFiltersCount > 0
                              ? 'Try relaxing your filters or clearing them.'
                              : 'Manual debits will appear here once admins perform them.'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      debits.map((d) => {
                        const rColor = reasonColor(d.reason_code);
                        return (
                          <TableRow
                            key={d.id}
                            hover
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setDetailDebit(d)}
                          >
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={1.2}>
                                <Avatar
                                  src={d.user_avatar || undefined}
                                  sx={{
                                    bgcolor: alpha('#c62828', 0.15),
                                    color: '#c62828',
                                    width: 32, height: 32, fontWeight: 800, fontSize: '0.72rem',
                                  }}
                                >
                                  {initials(d.holder_name)}
                                </Avatar>
                                <Typography fontWeight={700} sx={{
                                  fontFamily: '"Inter", sans-serif', fontSize: '0.83rem',
                                }}>
                                  {d.holder_name}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={d.staff_id}
                                sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.68rem' }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={reasonLabel(d.reason_code)}
                                sx={{
                                  fontWeight: 700, fontSize: '0.65rem',
                                  bgcolor: alpha(rColor, 0.1), color: rColor,
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 240 }}>
                              <Typography variant="caption" sx={{
                                display: 'block', color: '#475569',
                                overflow: 'hidden', textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap', fontFamily: '"Inter", sans-serif',
                              }}>
                                {d.reason_note || d.description || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{
                              fontWeight: 800, color: '#c62828',
                              fontFamily: '"Inter", sans-serif',
                            }}>
                              −{formatCurrency(d.amount)}
                            </TableCell>
                            <TableCell align="right" sx={{
                              fontWeight: 700, color: '#0F172A',
                              fontFamily: '"Inter", sans-serif',
                            }}>
                              {formatCurrency(d.balance_after)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                              {formatDateTime(d.occurred_at)}
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.72rem', color: '#64748B' }}>
                              {d.performed_by_name || '—'}
                            </TableCell>
                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                              <Tooltip title="View details">
                                <IconButton size="small" onClick={() => setDetailDebit(d)}>
                                  <ReceiptIcon fontSize="small" sx={{ color: '#1976d2' }} />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              />
            </Paper>
          </div>
        </div>
      </div>
      <Footer />

      {/* DETAIL DIALOG */}
      <DebitDetailDialog
        open={Boolean(detailDebit)}
        debit={detailDebit}
        onClose={() => setDetailDebit(null)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', fontWeight: 700, fontFamily: '"Inter", sans-serif' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default ManualDebits;